import {HostAppMessenger, HostAppRequestType} from './HostAppMessenger';
import {GetInitialMonthlyMinutesUsed} from './office-js/HostAppSettings';
import {AVAILABLE_UPLOAD_MINUTES_PER_MONTH} from '../AppConstants';
import { LogEvents } from '../utils/LogEvents';
import { MonthlyQuotaKeys } from '../backend/telemetry/MonthlyQuotaKeys'
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { LogErrorAndThrow } from '../utils/ErrorTelemetry';

// This class uses host app messaging to send messages to the host (WAC).
// WAC then handles our messages by making requests to its RoamingServiceHandler, which
// reads and writes values from the roaming service.
// Some pointers to where the code lives:
// Host message handling: wac/src/WordEditor/Client/Packages/Transcription/TranscriptionPaneLoader/SDXAddInLoader/TranscriptionAddInMessaging.cs
// Host quota handling: wac/src/WordEditor/Client/Packages/Transcription/TranscriptionPaneLoader/SDXAddInLoader/TranscriptionMonthlyQuotaHandler.cs
// TODO: Add location of RoamingServiceHandler handlers once they are checked in.
// Roaming service setting definitions (our setting names are ridTranscriptionQuotaMinutesUsed and ridTranscriptionQuotaLastResetTimestamp):
// https://office.visualstudio.com/ISS/_git/Service-Utilities?path=%2Fsrc%2FTransformer%2FRoamingDefinitions%2FRoamingDefinitions.xml
export class TranscriptionMonthlyQuota {
    private usedMinutesCache: number = 0;
    private hostMessenger: HostAppMessenger;

    private static instance: TranscriptionMonthlyQuota | null = null;

    static GetInstance() {
        if (this.instance == null)
            this.instance = new TranscriptionMonthlyQuota();

        return this.instance;
    }

    constructor() {
        LogEvents.MonthlyQuota.Start();
        this.usedMinutesCache = GetInitialMonthlyMinutesUsed();
        this.hostMessenger = new HostAppMessenger();
        LogEvents.MonthlyQuota.End([ { key: MonthlyQuotaKeys.InitialMinutesUsed, value: this.usedMinutesCache }]);
    }

    public GetCachedUsedMinutes(): number {
        return this.usedMinutesCache;
    }

    public GetCachedPercentTimeUsed(): number {
        return this.usedMinutesCache / AVAILABLE_UPLOAD_MINUTES_PER_MONTH;
    }

    public GetAvailableUploadMinutesPerMonth(): number {
        return AVAILABLE_UPLOAD_MINUTES_PER_MONTH;
    }

    public async AddMinutesUsed(numMinutesUsed: number): Promise<void> {
        LogEvents.MonthlyQuota.Start();
        const response = await this.hostMessenger.MakeRequest(HostAppRequestType.AddMinutesUsed, { numMinutesUsed });
        if (response.monthlyMinutesUsed == null || typeof response.monthlyMinutesUsed != 'number') {
            LogEvents.MonthlyQuota.End([{ key: MonthlyQuotaKeys.AddMinutesUsedSucceeded, value: false }]); 
            LogErrorAndThrow(LogEventNames.MonthlyQuota, new Error('Received an invalid response: ' + JSON.stringify(response)));
        }

        this.usedMinutesCache = response.monthlyMinutesUsed;
        LogEvents.MonthlyQuota.End([
            { key: MonthlyQuotaKeys.AddMinutesUsedSucceeded, value: true }, 
            { key: MonthlyQuotaKeys.MinutesUsedAdded, value: this.usedMinutesCache } ]);
    }
}